import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api, tokenStore, errorText } from "../lib/api";

const AuthContext = createContext(null);

export const AUTH_URL = process.env.REACT_APP_AUTH_URL || "https://auth.fleetpods.com";
export const DEMO = process.env.REACT_APP_DEMO_BYPASS === "1";
export const SAFE_NEXT = /^\/(?!\/)(?!auth\/callback)[A-Za-z0-9\-_/?=&.%]{0,300}$/;

export function roleHome(u) {
  if (u?.role === "admin" || (u?.role === "gov_agency" && u?.gov_status === "verified")) return "/gov";
  if (u?.role === "site_operator") return "/site-owners";
  if (u?.role === "fleet_owner" || u?.role === "fleet_manager") return "/fleet-owners";
  if (u?.role === "driver") return "/drivers";
  return "/dashboard";
}

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const accept = async (data) => {
    if (data?.mfa_required) return { ok: false, mfa: true, mfaToken: data.mfa_token };
    tokenStore.set(data);
    await checkAuth();
    return { ok: true };
  };

  const run = async (fn) => {
    try {
      return await accept((await fn()).data);
    } catch (e) {
      return { ok: false, error: errorText(e) };
    }
  };

  const login = (email, password) => run(() => api.post("/auth/login", { email, password }));
  const register = (payload) => run(() => api.post("/auth/register", payload));
  const googleAuth = (code) => run(() => api.post("/auth/google", { code }));
  const verifyMfa = (mfaToken, code) => run(() => api.post("/auth/mfa/verify", { mfa_token: mfaToken, code }));

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    tokenStore.clear();
    if (DEMO) setUser(null);
    else window.location.replace(`${AUTH_URL}/login`);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, checkAuth, login, register, logout, googleAuth, verifyMfa }}>
      {children}
    </AuthContext.Provider>
  );
}
